import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { toast } from 'react-toastify';
import UserService from '../../../Services/user.service.js';
import EnumsService from '../../../Services/enums.service.js';
import AuthService from '../../../Services/auth.service.js';
const ConflictResolutionDialog = ({ isVisible, onClose, conflicts, caseId, onResolved }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [selectedConflicts, setSelectedConflicts] = useState({});
    const [loading, setLoading] = useState(false);

    // Initialize all conflicts as selected
    React.useEffect(() => {
        if (conflicts && conflicts.length > 0) {
            const initialSelection = {};
            conflicts.forEach((conflict, index) => {
                initialSelection[index] = true;
            });
            setSelectedConflicts(initialSelection);
        }
    }, [conflicts]);

    const handleToggleSelection = (index) => {
        setSelectedConflicts(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleSelectAll = () => {
        const allSelected = {};
        conflicts.forEach((_, index) => {
            allSelected[index] = true;
        });
        setSelectedConflicts(allSelected);
    };

    const handleDeselectAll = () => {
        setSelectedConflicts({});
    };

    const handleMoveTransactions = async () => {
        const selectedConflictsList = conflicts.filter((_, index) => selectedConflicts[index]);
        
        if (selectedConflictsList.length === 0) {
            toast.warning(t('PleaseSelectAtLeastOneConflict') || 'Please select at least one conflict to resolve');
            return;
        }

        setLoading(true);
        const token = UserService.getCurrentToken();

        try {
            // Prepare migrations array for bulk operation
            const migrations = selectedConflictsList.map(conflict => ({
                oldCardId: conflict.existingCardId,
                newCardId: conflict.newCardId
            }));

            // Single bulk API call instead of multiple individual calls
            const response = await EnumsService.BulkMoveTransactionsBetweenCards(
                caseId,
                migrations,
                token
            );

            if (response.ok) {
                const result = await response.json();
                
                if (result.successCount > 0) {
                    toast.success(
                        t('TransactionsMovedSuccessfully') || 
                        `Successfully moved transactions for ${result.successCount} card(s)`
                    );
                }
                
                if (result.failureCount > 0) {
                    toast.error(
                        t('SomeTransactionsFailed') || 
                        `Failed to move transactions for ${result.failureCount} card(s)`
                    );
                    
                    // Log detailed errors
                    result.results?.filter(r => !r.success).forEach(r => {
                        console.error(`Failed to move ${r.oldCardId} → ${r.newCardId}: ${r.errorMessage}`);
                    });
                }

                if (onResolved) {
                    onResolved();
                }
                
                onClose();
            } else {
                const errorText = await response.text();
                console.error('Bulk move failed:', errorText);
                toast.error(t('ErrorMovingTransactions') || 'Error moving transactions');
            }
        } catch (error) {
            console.error('Error in handleMoveTransactions:', error);
            toast.error(t('ErrorMovingTransactions') || 'Error moving transactions');
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = () => {
        if (onResolved) {
            onResolved();
        }
        onClose();
    };

    if (!conflicts || conflicts.length === 0) {
        return null;
    }

    const selectedCount = Object.values(selectedConflicts).filter(Boolean).length;

    return (
        <Dialog
            open={isVisible}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            dir={direction}
        >
            <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #304FFF' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <InfoIcon sx={{ color: '#304FFF' }} />
                    <Typography variant="h6" component="span">
                        {t('AccountCardConflictsDetected') || 'Account Card Conflicts Detected'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        {t('ConflictExplanation') || 
                        'The following conflicts were detected during upload. Cards with the same ID but different names have been created with new IDs. Only cards with existing transactions are shown. You can optionally move all transactions from the old cards to the new cards.'}
                    </Typography>
                </Alert>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('ConflictsFound') || 'Conflicts Found'}: {conflicts.length}
                    </Typography>
                    <Box>
                        <Button 
                            size="small" 
                            onClick={handleSelectAll}
                            sx={{ mr: 1 }}
                        >
                            {t('SelectAll') || 'Select All'}
                        </Button>
                        <Button 
                            size="small" 
                            onClick={handleDeselectAll}
                        >
                            {t('DeselectAll') || 'Deselect All'}
                        </Button>
                    </Box>
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedCount === conflicts.length}
                                        indeterminate={selectedCount > 0 && selectedCount < conflicts.length}
                                        onChange={() => selectedCount === conflicts.length ? handleDeselectAll() : handleSelectAll()}
                                    />
                                </TableCell>
                                <TableCell><strong>{t('IncomingCardID') || 'Incoming Card ID'}</strong></TableCell>
                                <TableCell><strong>{t('IncomingName') || 'Incoming Name'}</strong></TableCell>
                                <TableCell align="center">
                                    <SwapHorizIcon />
                                </TableCell>
                                <TableCell><strong>{t('ExistingCardID') || 'Existing Card ID'}</strong></TableCell>
                                <TableCell><strong>{t('ExistingName') || 'Existing Name'}</strong></TableCell>
                                <TableCell><strong>{t('NewCardID') || 'New Card ID'}</strong></TableCell>
                                <TableCell align="center"><strong>{t('Transactions') || 'Transactions'}</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {conflicts.map((conflict, index) => (
                                <TableRow 
                                    key={index}
                                    hover
                                    selected={selectedConflicts[index]}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleToggleSelection(index)}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedConflicts[index] || false}
                                            onChange={() => handleToggleSelection(index)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </TableCell>
                                    <TableCell>{conflict.incomingCardId}</TableCell>
                                    <TableCell>{conflict.incomingName}</TableCell>
                                    <TableCell align="center">
                                        <SwapHorizIcon color="action" />
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            backgroundColor: '#fff3cd',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {conflict.existingCardId}
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            backgroundColor: '#fff3cd'
                                        }}
                                    >
                                        {conflict.existingName}
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            backgroundColor: '#d1f2eb',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {conflict.newCardId}
                                    </TableCell>
                                    <TableCell 
                                        align="center"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            color: conflict.transactionCount > 0 ? '#d32f2f' : '#757575'
                                        }}
                                    >
                                        {conflict.transactionCount || 0}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box mt={2}>
                    <Typography variant="body2" color="textSecondary">
                        {t('SelectedConflicts') || 'Selected'}: {selectedCount} / {conflicts.length}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ padding: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button
                    onClick={handleSkip}
                    variant="outlined"
                    disabled={loading}
                >
                    {t('SkipForNow') || 'Skip For Now'}
                </Button>
                <Button
                    onClick={handleMoveTransactions}
                    variant="contained"
                    color="primary"
                    disabled={loading || selectedCount === 0}
                    startIcon={loading ? <CircularProgress size={20} /> : <SwapHorizIcon />}
                >
                    {loading 
                        ? (t('MovingTransactions') || 'Moving Transactions...') 
                        : (t('MoveSelectedTransactions') || `Move Transactions (${selectedCount})`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConflictResolutionDialog;

